import React from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { StorefrontCard, StorefrontGridProps } from "./types";

/**
 * Storefront grid — the same app's store page across countries, side by side.
 *
 * Each card is a screenshot of the real store page taken in a phone-sized
 * viewport, so what is compared is the storefront itself: localized name and
 * subtitle, creatives, rating, age. One storefront is easy to open in a browser;
 * ten at once is the point of this block.
 *
 * Pure presentational: the host renders the frames, picks the countries and
 * decides what a click opens.
 */
export const StorefrontGrid: React.FC<StorefrontGridProps> = ({
  storefronts,
  loading = false,
  controlsSlot,
  onOpen,
  emptyText = "Pick the countries you want to see.",
  footnote,
  className,
}) => (
  <div className={cs(styles.grid, className)}>
    {controlsSlot && <div className={styles.controls}>{controlsSlot}</div>}

    {storefronts.length === 0 && !loading ? (
      <p className={styles.empty}>{emptyText}</p>
    ) : (
      <div className={styles.cards}>
        {storefronts.map((storefront) => (
          <Card
            key={storefront.country}
            storefront={storefront}
            loading={loading}
            onOpen={onOpen}
          />
        ))}
      </div>
    )}

    {footnote && <p className={styles.footnote}>{footnote}</p>}
  </div>
);

const Card: React.FC<{
  storefront: StorefrontCard;
  loading: boolean;
  onOpen?: (storefront: StorefrontCard) => void;
}> = ({ storefront, loading, onOpen }) => {
  const { country, label, flag, image, title, unavailable, failed } =
    storefront;
  const clickable = Boolean(onOpen && image);

  return (
    <figure className={styles.card}>
      <div
        className={cs(styles.frame, { [styles.frameClickable]: clickable })}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={() => clickable && onOpen?.(storefront)}
        onKeyDown={(event) => {
          if (clickable && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            onOpen?.(storefront);
          }
        }}
      >
        {image ? (
          <img
            className={styles.shot}
            src={image}
            alt={`${label ?? country} storefront`}
            loading="lazy"
          />
        ) : unavailable ? (
          <span className={styles.unavailable}>Not published here</span>
        ) : failed ? (
          // Never dressed up as "not published": that would be a claim about
          // the app, and this is a claim about us.
          <span className={styles.failed}>
            Could not render this storefront
          </span>
        ) : (
          <span className={cs(styles.placeholder, { [styles.pulse]: loading })}>
            {loading ? "Rendering…" : "No frame"}
          </span>
        )}
      </div>

      <figcaption className={styles.caption}>
        <span className={styles.country}>
          {flag && <span className={styles.flag}>{flag}</span>}
          {label ?? country.toUpperCase()}
        </span>
        {title && <span className={styles.title}>{title}</span>}
      </figcaption>
    </figure>
  );
};

export default StorefrontGrid;
